import { type RouterOutputs } from './trpc';

// What `auth.authMethods.resolve` tells the login screen about an account.
// `methods` is already in UI priority order (passkey, password, oauth).
export type ResolvedMethods = RouterOutputs['authMethods']['resolve'];

export type OAuthProviderName = 'GOOGLE' | 'APPLE';

export const ALL_PROVIDERS: OAuthProviderName[] = ['GOOGLE', 'APPLE'];
export const providerLabel = (p: OAuthProviderName) =>
  p === 'GOOGLE' ? 'Google' : 'Apple';

// Shown when the username is unknown (or the lookup is rate-limited): every
// method, so the form never confirms whether an account exists.
export const DEFAULT_METHODS: ResolvedMethods = {
  found: false,
  methods: ['passkey', 'password', 'oauth'],
  oauthProviders: ['GOOGLE', 'APPLE']
};

// The account's real methods when found, otherwise the full default set.
export function methodsToShow(resolved: ResolvedMethods): ResolvedMethods {
  return resolved.found && resolved.methods.length > 0
    ? resolved
    : DEFAULT_METHODS;
}

// A password-only account can skip straight to the password field.
export function isPasswordOnly(resolved: ResolvedMethods): boolean {
  return resolved.methods.length === 1 && resolved.methods[0] === 'password';
}

// An account with none of the four sign-in methods (password, passkey, Google,
// Apple) — e.g. one created by magic link. It has no way to sign in again, so
// it's forced through account setup to add one.
export function hasNoLoginMethod(user: {
  hasPassword: boolean;
  hasPasskey: boolean;
  oauthProviders: OAuthProviderName[];
}): boolean {
  return (
    !user.hasPassword && !user.hasPasskey && user.oauthProviders.length === 0
  );
}
